/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';

import { InfoCircleOutlined, MenuOutlined, OrderedListOutlined, WarningOutlined } from '@ant-design/icons';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Form, Input, Switch, Table, Tooltip, Typography } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { ColumnsType } from 'antd/lib/table';

import { useAppDispatch, useAppSelector } from '@/Hooks/useAppRedux';
import { ChannelData } from '@/Interfaces/I_vod';

import { getMallCategories } from '@/Redux/slices/PostManagementSlice';
import { updateChannels } from '@/Redux/slices/VodSlice';

import CreateChannelModal from '@/Components/CreateChannelModal';
import UnpinChannelModal from '@/Components/UnpinChannelModal';
import ChannelPostResequenceModal from './ChannelPostResequenceModal';

import { timeTranslate } from '@/Utils/timeTranslate';

import type { DragEndEvent } from '@dnd-kit/core';

interface ChannelUI {
  id: number;
  priority: number;
  mallCategoryId: number;
  mallCategoryName: string;
  mallCategoryCode: string;
  name: string;
  disabled: boolean;
  remark: string;
  postsCount: number;
  lastModifiedTime: number;
}

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
  isDragMode: boolean;
}

const Row = ({ isDragMode, children, ...props }: RowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes}>
      {React.Children.map(children, (child) => {
        if ((child as React.ReactElement).key === 'sort' && isDragMode) {
          return React.cloneElement(child as React.ReactElement<any>, {
            children: (
              <MenuOutlined
                ref={setActivatorNodeRef}
                style={{
                  touchAction: 'none',
                  cursor: isDragMode ? 'move' : 'not-allowed',
                }}
                {...(isDragMode ? listeners : {})}
              />
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

const ChannelPriority = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.vod.loading);
  const { channels, channelVersion } = useAppSelector((state) => state.vod.value);
  const mallCategories = useAppSelector((state) => state.postManagement.value.mallCategories);

  const [pristine, setPristine] = useState(true);
  const [channelUIs, setChannelUIs] = useState<ChannelUI[]>([]);
  const [editingChannel, setEditingChannel] = useState<ChannelUI | null>(null);
  const [isModalVisible, setModalVisible] = useState(false);
  const [isUnpinModalVisible, setUnpinModalVisible] = useState(false);
  const [managingChannel, setManagingChannel] = useState<ChannelData | null>(null);
  const [filteredInfo, setFilteredInfo] = useState<any>({});

  const [form] = useForm();

  const initChannelUIs = useCallback(() => {
    const sortByPriority = channels
      .toSorted((c1: ChannelData, c2: ChannelData) => {
        const mallCategoryId1 = c1.mallCategoryId ?? 0;
        const mallCategoryId2 = c2.mallCategoryId ?? 0;
        const priority1 = c1.priority ?? 0;
        const priority2 = c2.priority ?? 0;
        if (mallCategoryId1 !== mallCategoryId2) return mallCategoryId1 - mallCategoryId2;
        return priority1 - priority2;
      })
      .map((channel) => ({
        id: channel.channelId,
        priority: channel.priority,
        name: channel.channelName,
        disabled: channel.disabled,
        mallCategoryId: channel.mallCategoryId ?? 0,
        mallCategoryName: channel.mallCategoryName || '',
        mallCategoryCode: channel.mallCategoryCode || '',
        lastModifiedTime: channel.lastModifiedTime,
        postsCount: channel.postsCount || 0,
        remark: channel.remark || '',
      }));

    setChannelUIs(sortByPriority);
    setEditingChannel(null);
    setPristine(true);
  }, [channels]);

  useEffect(() => {
    if (!loading && channels.length > 0) {
      initChannelUIs();
    }
  }, [initChannelUIs, loading, channels]);

  useEffect(() => {
    if (!mallCategories) {
      dispatch(getMallCategories());
    }
  }, [dispatch, mallCategories]);

  const onChannelUIEditConfirmed = useCallback(() => {
    const fieldValues = form.getFieldsValue(true);

    setChannelUIs((prev) =>
      prev.map((channel) => {
        if (channel.id !== editingChannel?.id) {
          return channel;
        }

        return {
          ...channel,
          name: fieldValues.name,
          remark: fieldValues.remark,
          disabled: fieldValues.disabled,
        };
      }),
    );

    const updated =
      fieldValues.name !== editingChannel?.name ||
      fieldValues.remark !== editingChannel?.remark ||
      fieldValues.disabled !== editingChannel?.disabled;

    setPristine((prev) => !updated && prev);
    setEditingChannel(null);
  }, [
    editingChannel?.disabled,
    editingChannel?.id,
    editingChannel?.name,
    editingChannel?.remark,
    form,
  ]);

  const onUpdate = (edit: ChannelUI) => {
    const cloned = { ...edit };
    setEditingChannel(cloned);
    form.setFieldsValue(cloned);
  };

  const columns: ColumnsType<ChannelUI> = [
    {
      key: 'sort',
    },
    {
      dataIndex: 'remark',
      render: (_, channelUI) => !!channelUI.remark && <WarningOutlined />,
    },
    {
      title: 'Id',
      dataIndex: 'id',
    },
    {
      title: 'Channel',
      dataIndex: 'name',
      render: (_, channelUI) =>
        editingChannel?.id === channelUI.id ? (
          <Form.Item noStyle name="name">
            <Input />
          </Form.Item>
        ) : (
          channelUI.name
        ),
      width: '40%',
    },
    {
      title: (
        <span>
          Street&nbsp;&nbsp;
          <Tooltip title="Choose ONE street for prioritization.">
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'mallCategoryName',
      filters: mallCategories.map((mc) => ({
        text: mc.name,
        value: mc.categoryId,
      })),
      onFilter: (value, channel) => channel.mallCategoryCode === value,
      filteredValue: filteredInfo.mallCategoryName || null,
    },
    {
      title: 'Disabled',
      dataIndex: 'disabled',
      render: (_, channelUI) =>
        channelUI.id === editingChannel?.id ? (
          <Form.Item noStyle name="disabled" valuePropName="checked">
            <Switch />
          </Form.Item>
        ) : (
          <Switch disabled checked={channelUI.disabled} />
        ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, channelUI) =>
        editingChannel?.id === channelUI.id ? (
          <span>
            <Button onClick={() => onChannelUIEditConfirmed()} style={{ marginRight: 8 }}>
              Confirm
            </Button>
            <Button onClick={() => setEditingChannel(null)}>Cancel</Button>
          </span>
        ) : (
          <Typography.Link onClick={() => onUpdate(channelUI)}>Edit</Typography.Link>
        ),
      width: '5%',
    },
    {
      title: '# of Pinned Video',
      dataIndex: 'postsCount',
    },
    {
      title: 'Video Sequence',
      key: 'videoSequence',
      render: (_, channelUI) => (
        <Button
          size="small"
          disabled={channelUI.mallCategoryCode !== 'live'}
          onClick={() =>
            setManagingChannel({
              channelId: channelUI.id,
              channelName: channelUI.name,
              mallCategoryId: channelUI.mallCategoryId,
              mallCategoryCode: channelUI.mallCategoryCode,
              mallCategoryName: channelUI.mallCategoryName,
              lastModifiedTime: channelUI.lastModifiedTime,
              priority: channelUI.priority,
              postsCount: channelUI.postsCount,
              disabled: channelUI.disabled,
              remark: channelUI.remark,
            })
          }
        >
          <OrderedListOutlined />
        </Button>
      ),
    },
    {
      title: 'Remarks',
      key: 'remark',
      render: (_, channelUI) =>
        editingChannel?.id === channelUI.id ? (
          <Form.Item noStyle name="remark">
            <Input />
          </Form.Item>
        ) : (
          <Tooltip placement="leftTop" title={channelUI.remark}>
            <p className="line-clamp-3 break-all mb-0">{channelUI.remark}</p>
          </Tooltip>
        ),
      width: 300,
    },
    {
      title: 'Last Modified',
      dataIndex: 'lastModifiedTime',
      key: 'lastModifiedTime',
      width: 200,
      render: (date: number) => <div className="flex">{timeTranslate(date, true)}</div>,
    },
  ];

  const onUpdateChannelUIs = useCallback(() => {
    let currentMallCategoryId = 0;
    let priorityCount = 1;
    const updatedChannels: ChannelData[] = channelUIs.map((data, index) => {
      if (currentMallCategoryId !== data.mallCategoryId) {
        currentMallCategoryId = data.mallCategoryId;
        priorityCount = 1;
      } else {
        priorityCount += 1;
      }
      return {
        channelId: data.id,
        priority: priorityCount,
        channelName: data.name,
        disabled: data.disabled,
        mallCategoryId: data.mallCategoryId,
        mallCategoryCode: data.mallCategoryCode,
        mallCategoryName: data.mallCategoryName,
        lastModifiedTime: data.lastModifiedTime,
        postsCount: data.postsCount,
        remark: data.remark,
      };
    });

    dispatch(updateChannels({ data: updatedChannels, version: channelVersion }));
  }, [channelUIs, channelVersion, dispatch]);

  const onResetChannelUIs = useCallback(() => {
    initChannelUIs();
    setPristine(true);
    setFilteredInfo({});
  }, [initChannelUIs]);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setChannelUIs((previous) => {
        const activeIndex = previous.findIndex((i) => i.id === active.id);
        const overIndex = previous.findIndex((i) => i.id === over?.id);
        return arrayMove(previous, activeIndex, overIndex);
      });
      setPristine(false);
    }
  };

  return (
    <div className="h-auto bg-white p-6 pb-2">
      <div className="flex items-center pb-4 justify-between">
        <div className="flex">
          <div className="pr-2">
            <Button type="primary" onClick={onUpdateChannelUIs} disabled={pristine}>
              Update
            </Button>
          </div>
          <div>
            <Button type="primary" onClick={onResetChannelUIs} disabled={pristine}>
              Reset
            </Button>
          </div>
        </div>
        <div className="flex gap-2 justify-self-end">
          <Button danger onClick={() => setUnpinModalVisible(true)}>
            Unpin
          </Button>
          <Button type="primary" onClick={() => setModalVisible(true)}>
            Create
          </Button>
        </div>
      </div>
      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          // rowKey array
          items={channelUIs.map((i) => i.id)}
          strategy={verticalListSortingStrategy}
        >
          <Form form={form} component={false}>
            <Table
              components={{
                body: {
                  row: Row,
                },
              }}
              rowKey="id"
              columns={columns}
              dataSource={channelUIs}
              pagination={false}
              loading={loading}
              onChange={(_p, filters, _s, _e) => setFilteredInfo(filters)}
              onRow={(_r, _i): any => ({
                isDragMode:
                  filteredInfo.mallCategoryName && filteredInfo.mallCategoryName.length === 1,
              })}
            />
          </Form>
        </SortableContext>
      </DndContext>
      <CreateChannelModal isModalVisible={isModalVisible} setModalVisible={setModalVisible} />
      <UnpinChannelModal
        isModalVisible={isUnpinModalVisible}
        setModalVisible={setUnpinModalVisible}
      />
      <ChannelPostResequenceModal
        channel={managingChannel}
        open={!!managingChannel}
        onClose={() => setManagingChannel(null)}
      />
    </div>
  );
};

export default ChannelPriority;
